import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveTenantFromUrl } from '@low6-pickem/frontend-shared';
import { useGetTenant } from '@/hooks/useTenant';
import { OAuthAuthProvider } from '@/components/auth/OAuthAuthProvider';
import { ClerkTenantAuthProvider } from '@/components/auth/ClerkTenantAuthProvider';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';

export { useAuth } from '@/contexts/auth-context';
export type { AuthContextType } from '@/contexts/auth-context';

interface AuthProviderProps {
  children: ReactNode;
}

const TENANT_SLUG = resolveTenantFromUrl();
const ENV_CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const { data: tenant, isLoading, error } = useGetTenant(TENANT_SLUG);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center max-w-md px-6">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-white text-xl font-bold mb-2">{t('app.configError.title')}</h2>
          <p className="text-gray-400 mb-4">{t('app.configError.description')}</p>
        </div>
      </div>
    );
  }

  const usesClerk = tenant?.authProvider === 'clerk';
  const clerkPublishableKey = tenant?.clerkPublishableKey || ENV_CLERK_PUBLISHABLE_KEY;

  if (usesClerk) {
    if (!clerkPublishableKey) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-background">
          <div className="text-center max-w-md px-6">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-white text-xl font-bold mb-2">{t('app.configError.title')}</h2>
            <p className="text-gray-400 mb-4">{t('app.authFailed.clerkMissing')}</p>
          </div>
        </div>
      );
    }

    return (
      <ClerkTenantAuthProvider publishableKey={clerkPublishableKey}>
        {children}
      </ClerkTenantAuthProvider>
    );
  }

  return <OAuthAuthProvider>{children}</OAuthAuthProvider>;
};
